import React, { memo, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useIsMobile } from '@/hooks/use-mobile';

import MobileLayout from '@/components/MobileLayout';
import TabbedTerminal from '@/components/TabbedTerminal';
import { TerminalHostContext } from '@/components/terminalHost';
import { DragDropLayout } from '@/components/dragDrop/Layout';

type Props = {
  children: JSX.Element;
};

/**
 * Responsive layout wrapper.
 * Desktop: stable CSS Grid layout with drag-drop (components never unmount).
 * Mobile: bottom tab bar to switch between views.
 *
 * The terminal is rendered once here (not inside the layouts) and re-parented
 * into the active layout's slot via `TerminalSlot`, so switching between the
 * mobile and desktop layouts never unmounts it or drops its SSH connection.
 */
const DynamicLayout = memo(function DynamicLayout({ children }: Props) {
  const isMobile = useIsMobile();
  const hostRef = useRef<HTMLDivElement>();
  if (!hostRef.current) {
    hostRef.current = document.createElement('div');
    hostRef.current.className = 'h-full w-full';
  }
  const host = hostRef.current;

  return (
    <TerminalHostContext.Provider value={host}>
      {createPortal(<TabbedTerminal className="h-full w-full" />, host)}
      {isMobile ? (
        <MobileLayout>{children}</MobileLayout>
      ) : (
        <div className="h-full w-full bg-background pl-1">
          <DragDropLayout>{children}</DragDropLayout>
        </div>
      )}
    </TerminalHostContext.Provider>
  );
});

export default DynamicLayout;
